import { useEffect, useRef, useState } from 'react'
import {
  ClipboardTextIcon,
  EyedropperSampleIcon,
  Mtds,
  SaveError,
  PackageIcon,
  PencilSimpleIcon,
  TagIcon,
  ThermometerIcon,
  type SinglePlan,
  buildUpdatePlanBody,
  useGetPlanByIdQuery,
  useAuth,
  useUpdatePlanMutation
} from 'prove-common'

type InstructionKey =
  | 'samplingInstructions'
  | 'storageInstructions'
  | 'labelingInstructions'
  | 'shippingInstructions'
  | 'followUpInstructions'

type InstructionSection = {
  key: InstructionKey
  title: string
  label: string
  help: string
  icon: typeof EyedropperSampleIcon
}

type InstructionDraft = {
  value: string
  baseline: SinglePlan
}

const sections: InstructionSection[] = [
  {
    key: 'samplingInstructions',
    title: 'Hvordan ta ut prøven',
    label: 'Framgangsmåte',
    help: 'Hvordan selve uttaket gjøres.',
    icon: EyedropperSampleIcon
  },
  {
    key: 'storageInstructions',
    title: 'Hvordan oppbevare prøven',
    label: 'Oppbevaring',
    help: 'Hvordan prøven skal oppbevares fram til den sendes.',
    icon: ThermometerIcon
  },
  {
    key: 'labelingInstructions',
    title: 'Hvordan merke prøven',
    label: 'Merking',
    help: 'Hvilken informasjon prøven skal merkes med.',
    icon: TagIcon
  },
  {
    key: 'shippingInstructions',
    title: 'Hvordan sende prøven',
    label: 'Forsendelse',
    help: 'Hvordan prøven skal pakkes og sendes til laboratoriet.',
    icon: PackageIcon
  },
  {
    key: 'followUpInstructions',
    title: 'Hvordan følge opp prøvesvaret',
    label: 'Oppfølging',
    help: 'Hva inspektøren skal gjøre når prøvesvaret foreligger.',
    icon: ClipboardTextIcon
  }
]

type PlanInstructionsTabProps = {
  plan: SinglePlan
  onDirtyChange?: (isDirty: boolean) => void
}

export function PlanInstructionsTab({
  plan,
  onDirtyChange
}: Readonly<PlanInstructionsTabProps>) {
  const { isAdmin, permissions = [] } = useAuth()
  const canEdit =
    plan.canManage &&
    (isAdmin ||
      permissions.some(
        (permission) => permission.responsibility === 'PROGRAM_MANAGER'
      ))
  const latestPlanQuery = useGetPlanByIdQuery(plan.id)
  const updatePlan = useUpdatePlanMutation()
  const isSaving = useRef(false)
  const isRefreshingPlan = useRef(false)
  const [isSavingSection, setSavingSection] = useState(false)
  const [isRefreshing, setRefreshing] = useState(false)
  const confirmedPlan = latestPlanQuery.data ?? plan
  const [drafts, setDrafts] = useState<
    Partial<Record<InstructionKey, InstructionDraft>>
  >({})
  const [saveErrors, setSaveErrors] = useState<
    Partial<Record<InstructionKey, unknown>>
  >({})
  const [refreshErrors, setRefreshErrors] = useState<
    Partial<Record<InstructionKey, string>>
  >({})
  const dirtyKeys = sections
    .filter(
      ({ key }) =>
        drafts[key] != null &&
        drafts[key].value !== (drafts[key].baseline[key] ?? '')
    )
    .map(({ key }) => key)

  useEffect(() => {
    onDirtyChange?.(dirtyKeys.length > 0)
  }, [dirtyKeys.length, onDirtyChange])

  const startEditing = (section: InstructionSection) => {
    setRefreshErrors((current) => ({ ...current, [section.key]: undefined }))
    if (drafts[section.key] == null) {
      setDrafts((current) => ({
        ...current,
        [section.key]: {
          value: confirmedPlan[section.key] ?? '',
          baseline: confirmedPlan
        }
      }))
    }
    setSaveErrors((current) => ({ ...current, [section.key]: undefined }))
  }

  const cancelEditing = (key: InstructionKey) => {
    setDrafts((current) => {
      const next = { ...current }
      delete next[key]
      return next
    })
    setSaveErrors((current) => ({ ...current, [key]: undefined }))
    setRefreshErrors((current) => ({ ...current, [key]: undefined }))
  }

  const updateDraft = (key: InstructionKey, value: string) => {
    setDrafts((current) => {
      const draft = current[key]
      return draft == null
        ? current
        : { ...current, [key]: { ...draft, value } }
    })
  }

  const refreshLatestPlan = async (key: InstructionKey) => {
    if (isRefreshingPlan.current) return
    isRefreshingPlan.current = true
    setRefreshing(true)
    setRefreshErrors((current) => ({ ...current, [key]: undefined }))
    try {
      const result = await latestPlanQuery.refetch()
      if (result.isError || result.data == null) {
        throw result.error ?? new Error('Missing refreshed plan response')
      }
      const refreshedPlan = result.data
      setDrafts((current) => {
        const draft = current[key]
        return draft == null
          ? current
          : { ...current, [key]: { ...draft, baseline: refreshedPlan } }
      })
      setSaveErrors((current) => ({ ...current, [key]: undefined }))
    } catch {
      setRefreshErrors((current) => ({
        ...current,
        [key]: 'Kunne ikke hente siste plan. Prøv igjen.'
      }))
    } finally {
      isRefreshingPlan.current = false
      setRefreshing(false)
    }
  }

  const saveEditing = async (section: InstructionSection) => {
    const selectedDraft = drafts[section.key]
    // A new ETag is safe only if this section still matches its edit baseline.
    // Pair the snapshot with its ETag so a full-plan PUT cannot replay newer
    // fields against an older version (or overwrite a concurrent section edit).
    const savePlan =
      selectedDraft != null &&
      (confirmedPlan[section.key] ?? '') ===
        (selectedDraft.baseline[section.key] ?? '')
        ? confirmedPlan
        : selectedDraft?.baseline
    if (
      updatePlan.isPending ||
      isSaving.current ||
      isRefreshingPlan.current ||
      savePlan?.etag == null ||
      selectedDraft == null
    ) {
      return
    }
    isSaving.current = true
    setSavingSection(true)
    try {
      await updatePlan.mutateAsync({
        planId: savePlan.id,
        etag: savePlan.etag,
        body: buildUpdatePlanBody(savePlan, {
          [section.key]: selectedDraft.value
        })
      })
      setDrafts((current) => {
        const next = { ...current }
        delete next[section.key]
        return next
      })
      setSaveErrors((current) => ({ ...current, [section.key]: undefined }))
      setRefreshErrors((current) => ({ ...current, [section.key]: undefined }))
    } catch (error) {
      setSaveErrors((current) => ({
        ...current,
        [section.key]: error
      }))
    } finally {
      isSaving.current = false
      setSavingSection(false)
    }
  }

  return (
    <Mtds.Grid data-gap='5'>
      <Mtds.Heading as='h2' data-size='sm'>
        Instruksjoner til inspektør
      </Mtds.Heading>
      <Mtds.Steps data-direction='down'>
        {sections.map((section) => {
          const Icon = section.icon
          const value = confirmedPlan[section.key]
          const isEditing = drafts[section.key] != null
          const draft = drafts[section.key]?.value ?? ''
          const saveError = saveErrors[section.key]
          return (
            <li key={section.key}>
              <mark>
                <Icon aria-hidden />
              </mark>
              <Mtds.Grid data-gap='3'>
                <Mtds.Heading as='h3' data-size='2xs'>
                  {section.title}
                </Mtds.Heading>
                <Mtds.Card data-size='sm' data-pad='4'>
                  {isEditing ? (
                    <Mtds.Grid data-gap='3'>
                      <Mtds.Field
                        as='textarea'
                        label={section.label}
                        description={section.help}
                        value={draft}
                        disabled={isSavingSection}
                        onChange={(event) =>
                          updateDraft(section.key, event.currentTarget.value)
                        }
                        rows={5}
                      />
                      <SaveError
                        error={saveError}
                        action='lagre instruksjonen'
                      />
                      {saveError != null && isPlanConflict(saveError) && (
                        <Mtds.Button
                          type='button'
                          onClick={() => void refreshLatestPlan(section.key)}
                          disabled={isRefreshing}
                          aria-busy={isRefreshing}
                        >
                          {isRefreshing
                            ? 'Henter siste plan…'
                            : 'Hent siste plan'}
                        </Mtds.Button>
                      )}
                      {refreshErrors[section.key] && (
                        <Mtds.Alert data-color='danger'>
                          {refreshErrors[section.key]}
                        </Mtds.Alert>
                      )}
                      <Mtds.Flex data-gap='3' data-justify='end'>
                        <Mtds.Button
                          type='button'
                          onClick={() => cancelEditing(section.key)}
                          disabled={isSavingSection}
                        >
                          Avbryt
                        </Mtds.Button>
                        <Mtds.Button
                          type='button'
                          data-variant='primary'
                          onClick={() => void saveEditing(section)}
                          disabled={isSavingSection || isRefreshing}
                          aria-busy={isSavingSection || isRefreshing}
                          aria-label={`Lagre ${section.title}`}
                        >
                          Lagre
                        </Mtds.Button>
                      </Mtds.Flex>
                    </Mtds.Grid>
                  ) : (
                    <Mtds.Flex data-align='start' data-justify='space-between'>
                      <p className='whitespace-pre-wrap'>
                        {value?.trim() ? value : 'Ikke fylt ut'}
                      </p>
                      {canEdit && (
                        <Mtds.Button
                          type='button'
                          data-variant='tertiary'
                          aria-label={`Rediger ${section.title}`}
                          onClick={() => startEditing(section)}
                        >
                          <PencilSimpleIcon aria-hidden />
                        </Mtds.Button>
                      )}
                    </Mtds.Flex>
                  )}
                </Mtds.Card>
              </Mtds.Grid>
            </li>
          )
        })}
      </Mtds.Steps>
    </Mtds.Grid>
  )
}

function isPlanConflict(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error != null &&
    'status' in error &&
    error.status === 412
  )
}
